/**
 * Indexation fractionnaire des positions.
 *
 * Pourquoi : réordonner en renumérotant des entiers modifierait N lignes, donc
 * produirait N écritures et N conflits potentiels — c'est le seul endroit où le
 * modèle de fusion ligne à ligne se comporterait mal hors ligne. En prenant la
 * moyenne des positions voisines, **un déplacement ne modifie qu'une ligne**.
 *
 * Limite : la précision des flottants double s'épuise après une cinquantaine
 * d'insertions consécutives exactement au même endroit. `needsRenumber` détecte
 * le cas et le code appelant déclenche alors une renumérotation complète de la
 * liste — opération rare, explicite, et qui reste correcte si elle entre en
 * conflit (elle sera simplement rejouée).
 */

/** Écart nominal entre deux positions consécutives. */
export const POSITION_GAP = 1024;

/**
 * En deçà de cet écart, la moyenne de deux positions n'est plus fiablement
 * distincte de ses bornes. Très au-dessus de l'epsilon machine, par prudence.
 */
export const MIN_POSITION_GAP = 1e-6;

/**
 * Calcule une position strictement comprise entre `before` et `after`.
 * `null` signifie « pas de voisin de ce côté » (début ou fin de liste).
 */
export function positionBetween(before: number | null, after: number | null): number {
  if (before === null && after === null) return 0;
  if (before === null) return (after as number) - POSITION_GAP;
  if (after === null) return before + POSITION_GAP;
  return before + (after - before) / 2;
}

/**
 * Indique si l'espace entre deux voisins est trop étroit pour y insérer.
 * Le code appelant doit alors renuméroter la liste avant d'insérer.
 */
export function needsRenumber(before: number | null, after: number | null): boolean {
  if (before === null || after === null) return false;
  return Math.abs(after - before) < MIN_POSITION_GAP;
}

/**
 * Réattribue des positions régulièrement espacées, en conservant l'ordre.
 * Renvoie uniquement les lignes dont la position change, pour n'émettre que
 * les écritures nécessaires.
 */
export function renumber<T extends { id: string; position: number }>(
  rows: readonly T[],
): { id: string; position: number }[] {
  const sorted = [...rows].sort(comparePosition);
  const changed: { id: string; position: number }[] = [];

  sorted.forEach((row, index) => {
    const next = (index + 1) * POSITION_GAP;
    if (row.position !== next) changed.push({ id: row.id, position: next });
  });

  return changed;
}

/**
 * Ordre d'affichage. L'`id` départage les positions égales, ce qui garantit un
 * ordre **identique sur les deux appareils** : sans ce départage, deux articles
 * créés à la même position s'afficheraient dans un ordre différent de chaque
 * côté, ce qui ressemblerait à un bug de synchronisation.
 */
export function comparePosition<T extends { id: string; position: number }>(a: T, b: T): number {
  if (a.position !== b.position) return a.position - b.position;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Position à utiliser pour ajouter en fin de liste. */
export function positionAtEnd(rows: readonly { position: number }[]): number {
  if (rows.length === 0) return POSITION_GAP;
  return Math.max(...rows.map((r) => r.position)) + POSITION_GAP;
}
